import { useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material'
import { computeNdaHash } from './ndaText'
import { useMobileDialogProps } from '@components/common/useMobileDialogProps'

interface PublishNdaDialogProps {
  open: boolean
  onClose: () => void
  onConfirm: () => void
  isSaving: boolean
  title: string
  body: string
  /** 0 when nothing has been published yet, in which case this publish turns the gate on. */
  currentVersion: number
  nextVersion: number
  /** People who accepted the version in force — the ones being asked to sign again. */
  acceptedCurrentCount: number
}

/**
 * The confirmation for a publish.
 *
 * Publishing is not "saving a document": it revokes everybody's access to the app until they
 * accept the new text. That deserves a stop, and the stop should say who it affects rather than
 * asking "are you sure?".
 */
export function PublishNdaDialog({
  open,
  onClose,
  onConfirm,
  isSaving,
  title,
  body,
  currentVersion,
  nextVersion,
  acceptedCurrentCount,
}: PublishNdaDialogProps) {
  const mobileDialogProps = useMobileDialogProps()
  const [hash, setHash] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    let cancelled = false
    computeNdaHash(title, body).then((value) => {
      if (!cancelled) setHash(value)
    })
    return () => {
      cancelled = true
    }
  }, [open, title, body])

  const firstPublish = currentVersion === 0

  return (
    <Dialog open={open} onClose={isSaving ? undefined : onClose} maxWidth="sm" fullWidth {...mobileDialogProps}>
      <DialogTitle>Publish version {nextVersion}?</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          {firstPublish ? (
            <>
              The app is ungated today. The moment version 1 exists, <strong>every user</strong> is
              held at the acceptance screen and cannot use the app until they accept it.
            </>
          ) : (
            <>
              <strong>Every user</strong> is sent back to the acceptance screen and cannot use the
              app until they accept version {nextVersion} — including the {acceptedCurrentCount}{' '}
              {acceptedCurrentCount === 1 ? 'person' : 'people'} who accepted version{' '}
              {currentVersion}.
            </>
          )}
        </Alert>

        <DialogContentText component="div">
          <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
            {!firstPublish && (
              <li>
                Version {currentVersion} is not modified or withdrawn. It stays on the record with
                its signatures, exactly as it was accepted.
              </li>
            )}
            <li>There is no way to unpublish. Correcting it means publishing version {nextVersion + 1}.</li>
            <li>Nobody is emailed. People meet the new agreement the next time they open the app.</li>
          </ul>
        </DialogContentText>

        {hash && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              The text people will be signing hashes to
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
              {hash}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button variant="contained" color="warning" onClick={onConfirm} disabled={isSaving}>
          {isSaving ? 'Publishing…' : `Publish version ${nextVersion}`}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
